/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDescriptionColumn.tsx
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Icon from 'src/components/Icon'
import {
  font,
  paragraphMedium,
  paragraphSmall,
  weightSemiBold,
} from 'src/components/css'

interface EventDescriptionColumnProps {
  description: string | null
}

/**
 * Left column of the detail page.
 *
 * The card deliberately does NOT repeat the event name — the page header
 * already carries it two lines above, and echoing it made the panel read as
 * padding rather than content.
 */
const EventDescriptionColumn: React.FC<EventDescriptionColumnProps> = ({
  description,
}) => {
  const body = description?.trim()

  return (
    <section>
      <h2 css={sectionLabelStyle}>{capitalizeWords('About This Event')}</h2>
      <div css={cardStyle}>
        {body ? (
          <p css={bodyStyle}>{body}</p>
        ) : (
          // Centred icon + muted line, so an event with no notes reads as
          // deliberately empty rather than as a failed render.
          <div css={emptyStyle}>
            <Icon name='align-left' fontSize={20} color={colors.NEUTRAL_50} />
            <span>{capitalizeWords('No Description Provided')}.</span>
          </div>
        )}
      </div>
    </section>
  )
}

export default EventDescriptionColumn

// ---- styled ----
/* Matches the game overview's section labels (see components/SignInSheetCard)
   — grey, uppercase, wide letterspacing — so event and game screens share one
   section-heading treatment. */
const sectionLabelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_50};
  margin: 0 0 12px;
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
`

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-radius: 16px;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SOFT_STEEL};
`

const emptyStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 0;
  ${font.body}
  ${paragraphMedium}
  color: ${colors.NEUTRAL_50};
  text-align: center;
`

const bodyStyle = css`
  ${font.body}
  ${paragraphMedium}
  color: ${colors.SOLITUDE};
  margin: 0;
  line-height: 1.6;
  white-space: pre-wrap;
  /* Free text can contain a long unbroken token (a pasted URL); break it rather
     than letting it push the card sideways. */
  overflow-wrap: anywhere;
`
